/**
 * Vandhane-konfigurator: betinget udtrækshane-logik + pr.-produkt funktionsbegrænsning.
 *
 * Udtrækshane (udtrækkelig tud) kan ikke kombineres med brus. Når kunden vælger
 * en funktion med brus, skal Udtrækshane-"Ja" gråes ud (synlig men låst) med en
 * forklarende info-tekst.
 *
 * DEFENSIVT (fase 1): udtrækshane-optionen findes IKKE på produkterne endnu - den
 * oprettes i fase 2. Komponenten laver early return hvis den ikke finder både en
 * funktion-option og en udtrækshane-option, så den er inert indtil optionen findes.
 * Rører ikke Horizons variant-picker-kerne; læser kun radios og sætter disabled.
 *
 * Kobler til den eksisterende variant-picker (snippets/variant-main-picker.liquid):
 * hver option er et <fieldset class="variant-option">, hver værdi en radio-input
 * med value = option-værdien.
 *
 * VIGTIGT - Horizon re-rendrer variant-pickerens indre HTML (Section Rendering API
 * morph) hver gang kunden skifter en variant (farve/form/funktion), for at opdatere
 * hvilke værdier der er "available" for den nye kombination. Det fjerner alt DOM vi
 * selv har tilføjet (info-bokse, disabled-attributter) og udskifter det med friske
 * elementer. Derfor kan init() ikke køre én gang - se initAll() nederst, som lytter
 * på ThemeEvents.variantUpdate og re-anvender begge klassers logik efter hver morph.
 */

import { ThemeEvents } from '@theme/events';

const BRUS_MARKER = 'brus';
const UDTRAEK_JA_VALUES = ['Ja', 'Med udtrækshane', 'Udtrækshane'];
const INFO_TEXT =
  'Træk-ud funktionen er kun tilgængelig i kombination med kogende og/eller ' +
  'afkølet vand, og kan ikke vælges sammen med brus.';

class VandhaneKonfigurator {
  constructor(picker) {
    this.picker = picker;
    this.functionInputs = [];
    this.udtraekJaInput = null;
    this.udtraekLabel = null;
    this.infoEl = null;
  }

  /** Find funktion- og udtrækshane-radios. Returnerer false hvis strukturen mangler. */
  resolve() {
    const inputs = Array.from(this.picker.querySelectorAll('input[type="radio"][value]'));
    if (inputs.length === 0) return false;

    // Funktion = enhver radio hvis value nævner "brus" (den fulde løsning findes altid
    // hvis produktet har brus-trinnet). Vi samler alle radios i samme fieldset.
    const brusInput = inputs.find((i) => i.value.toLowerCase().includes(BRUS_MARKER));
    if (!brusInput) return false;
    const functionFieldset = brusInput.closest('fieldset, .variant-option');
    if (!functionFieldset) return false;
    this.functionInputs = Array.from(
      functionFieldset.querySelectorAll('input[type="radio"][value]')
    );

    // Udtrækshane-"Ja" = radio i et ANDET fieldset hvis value matcher ja-listen.
    this.udtraekJaInput =
      inputs.find(
        (i) =>
          i.closest('fieldset, .variant-option') !== functionFieldset &&
          UDTRAEK_JA_VALUES.some((v) => i.value.toLowerCase() === v.toLowerCase())
      ) || null;
    if (!this.udtraekJaInput) return false;

    this.udtraekLabel = this.udtraekJaInput.closest('label');
    return true;
  }

  /** Er en funktion med brus valgt netop nu? */
  brusSelected() {
    const checked = this.functionInputs.find((i) => i.checked);
    return checked ? checked.value.toLowerCase().includes(BRUS_MARKER) : false;
  }

  ensureInfoEl() {
    if (this.infoEl) return this.infoEl;
    const el = document.createElement('p');
    el.className = 'vandhane-konfigurator__info';
    el.hidden = true;
    el.textContent = INFO_TEXT;
    const fieldset = this.udtraekJaInput.closest('fieldset, .variant-option');
    (fieldset || this.picker).append(el);
    this.infoEl = el;
    return el;
  }

  apply = () => {
    const lock = this.brusSelected();
    const info = this.ensureInfoEl();

    this.udtraekJaInput.disabled = lock;
    this.udtraekJaInput.setAttribute('aria-disabled', String(lock));
    if (this.udtraekLabel) this.udtraekLabel.classList.toggle('is-locked', lock);
    info.hidden = !lock;

    // Hvis brus valgt mens "Ja" var markeret: fald tilbage til Nej, så kurven
    // ikke sender en ugyldig kombination.
    if (lock && this.udtraekJaInput.checked) {
      const nej = this.functionInputs.length
        ? this.udtraekJaInput
            .closest('fieldset, .variant-option')
            ?.querySelector('input[type="radio"]:not([value="' + this.udtraekJaInput.value + '"])')
        : null;
      if (nej) {
        nej.checked = true;
        nej.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  };

  init() {
    if (!this.resolve()) return; // inert indtil udtrækshane-optionen findes (fase 2)
    this.functionInputs.forEach((i) => {
      if (i.dataset.vhkBound) return; // undgå dobbelt-lytter ved gentagne init() på samme DOM
      i.dataset.vhkBound = 'true';
      i.addEventListener('change', this.apply);
    });
    this.apply();
  }
}

/**
 * Pr.-produkt funktionsbegrænsning.
 *
 * 3-1 og 4-1 er fysisk IKKE i stand til at levere alle funktionstrin, selvom
 * Shopify-varianten har prisdata for dem (variant-strukturen deles med 5-1).
 * Skjuler de utilgængelige trin helt (ikke bare udgråning - kunden må ikke
 * kunne vælge dem) og viser en info-boks med link til den model der kan.
 *
 * Identificerer produktet via data-product-url på <variant-picker> (sat af
 * snippets/variant-main-picker.liquid), ikke window.location - robust også
 * hvis komponenten nogensinde renderes uden for hovedsidens URL (quick-view).
 */

const FUNKTION_WHITELIST = [
  {
    match: '2-4-liter-cooker-inkl-hane-filter', // 3-1
    // 3-1 leverer ÉN funktion. Brus er ikke en af dem: brus fås kun sammen med
    // nedkøling, og de brus-varianter der kort fandtes her blev slettet 29-08-2026.
    allowed: ['Kogende vand', 'Nedkølet vand'],
    infoHtml:
      'Denne model leverer enten kogende vand eller nedkølet vand - ikke begge på samme ' +
      'tid. Ønsker du kogende og nedkølet vand fra samme hane, eller brus, så se ' +
      '<a href="/products/2-4-liter-cooker-inkl-udtraekshane-filter">4-1-modellen</a> ' +
      'eller <a href="/products/2-4-liter-5-1-inkl-hane-filter">5-1-modellen</a>.',
  },
  {
    match: '2-4-liter-cooker-inkl-udtraekshane-filter', // 4-1
    // "Nedkølet vand" manglede her, selvom de 5 varianter findes til 8.900 kr —
    // de var derfor skjult på produktsiden ved en fejl. Rettet 28-08-2026.
    allowed: ['Kogende vand', 'Nedkølet vand', 'Kogende og nedkølet vand'],
    infoHtml:
      'Denne model leveres med kogende vand, nedkølet vand, eller begge dele - og kan ' +
      'opgraderes senere, hvis du starter med kun én funktion. Ønsker du kogende, nedkølet ' +
      'og brusende vand fra samme hane, så se <a href="/products/2-4-liter-5-1-inkl-hane-filter">5-1-modellen</a>.',
  },
];

// Substrings der identificerer en radio som hørende til "funktion"-fieldsettet, uafhængigt
// af hvilket cooker-produkt vi er på (3-1/4-1/5-1 har hver deres eget værdi-sæt - se plan:
// 4-1 har fx en selvstændig "Nedkølet vand"-værdi som 3-1/5-1 ikke har).
const FUNKTION_VALUE_MARKERS = ['kogende', 'nedkølet', 'brus'];

class FunktionsBegraensning {
  constructor(picker) {
    this.picker = picker;
  }

  resolveConfig() {
    const productUrl = this.picker.dataset.productUrl || '';
    return FUNKTION_WHITELIST.find((entry) => productUrl.includes(entry.match)) || null;
  }

  init() {
    const config = this.resolveConfig();
    if (!config) return; // produkt ikke i whitelisten (fx 5-1) → uændret, alle trin vises

    const inputs = Array.from(this.picker.querySelectorAll('input[type="radio"][value]'));
    const functionInputs = inputs.filter((i) => {
      const value = i.value.toLowerCase();
      return FUNKTION_VALUE_MARKERS.some((marker) => value.includes(marker));
    });
    if (functionInputs.length === 0) return;

    let hiddenCount = 0;
    let fieldset = null;
    functionInputs.forEach((input) => {
      const label = input.closest('label');
      fieldset = input.closest('fieldset, .variant-option') || fieldset;
      if (!config.allowed.includes(input.value)) {
        // Kun visuel skjulning (hidden), IKKE input.disabled = true. Horizons
        // variant-picker.js sporer valgt/tidligere-valgt state via egne
        // #radios/#checkedIndices-arrays indekseret på DOM-position - at gøre
        // en radio disabled ændrer formularens native indsendelses-/fokus-
        // semantik og kan forstyrre den interne state-tracking (mistænkt
        // årsag til at form-skift (C/U) blev uklikbart efter denne ændring).
        // [hidden]-CSS-fixet forhindrer allerede klik/synlighed for kunden.
        if (label) label.hidden = true;
        hiddenCount += 1;
      }
    });

    if (hiddenCount === 0 || !fieldset) return;

    // Guard mod duplikering hvis init() køres igen på samme (ikke-morphet) DOM.
    const existing = fieldset.querySelector('.vandhane-konfigurator__info--funktion');
    if (existing) return;

    const info = document.createElement('p');
    info.className = 'vandhane-konfigurator__info vandhane-konfigurator__info--funktion';
    info.innerHTML = config.infoHtml;
    fieldset.append(info);
  }
}

/** Kør begge komponenter på én variant-picker. */
function applyTo(picker) {
  new VandhaneKonfigurator(picker).init();
  new FunktionsBegraensning(picker).init();
}

function applyAll() {
  document.querySelectorAll('variant-picker').forEach(applyTo);
}

/**
 * Observér hver variant-picker for DOM-mutationer og genanvend vores logik.
 *
 * Vi brugte først ThemeEvents.variantUpdate, men det event dispatches ikke
 * pålideligt for alle variant-skift (kun ved visse flows i variant-picker.js).
 * MutationObserver er uafhængig af Horizons interne event-flow: den fanger
 * ALT DOM-morph på pickeren, uanset hvordan det trigges.
 *
 * Re-entry-guard: vores egne skrivninger (hidden, disabled, tilføjet <p>) ville
 * ellers trigge observeren igen. Vi disconnect'er observeren mens vi selv
 * skriver til DOM'et og genforbinder bagefter - det er vandtæt modsat et
 * boolean-flag, som kunne nulstilles midt i vores egne mutations-callbacks.
 */
function observePicker(picker) {
  if (picker.dataset.vhkObserved) return;
  picker.dataset.vhkObserved = 'true';

  let scheduled = false;

  const observer = new MutationObserver(() => {
    // Horizons morph() kan mutere DOM'et i flere separate batches (fjerne gamle
    // noder, indsætte nye noder er typisk ≥2 mutation-records). Kører vi applyTo
    // synkront på første batch, arbejder vi på et halvfærdigt træ og kan komme i
    // vejen for morph's egne efterfølgende skrivninger til de samme noder - det
    // var årsagen til at formen (C/U) blev ved med at være uklikbar. rAF venter
    // til browseren har flushet ALT synkront DOM-arbejde for framen, så vi kun
    // kører når morph reelt er færdig.
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      observer.disconnect();
      applyTo(picker);
      observer.observe(picker, { childList: true, subtree: true });
    });
  });

  observer.observe(picker, { childList: true, subtree: true });
}

function initAll() {
  applyAll();
  document.querySelectorAll('variant-picker').forEach(observePicker);

  // Behold variant:update som sekundær sti (billigt, ingen skade hvis den også fyrer).
  document.addEventListener(ThemeEvents.variantUpdate, applyAll);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll, { once: true });
} else {
  initAll();
}
